import React from "react";
import LearnLayout from "@/components/layout/LearnLayout";
import {
  generateArticleSEO,
  generateFAQSchema,
  generateArticleSchema,
} from "@/lib/seo";
import {
  Shield,
  Heart,
  CheckCircle,
  XCircle,
  MessageSquare,
  Zap,
} from "lucide-react";
import GuideCallout from "@/components/seo/GuideCallout";

const seo = generateArticleSEO({
  title: "Why Durmah Will Never Sell Better Grades",
  description:
    "A founder's note on the line we will not cross: no premium tier, no academic advantage for sale, no pay-to-perform. Why Durmah monetises support, not shortcuts.",
  slug: "why-we-dont-sell-better-grades",
  path: "/articles/why-we-dont-sell-better-grades",
  keywords:
    "ethical AI study tools, no premium answers, fair AI for students, pay to perform, AI law students, Durmah ethics",
});

const faqs = [
  {
    question: "Will Durmah ever launch a premium tier with better academic answers?",
    answer:
      "No. The Ethical Monetisation System is written into Durmah's product doctrine and checked into the codebase. Academic capability is the equality floor for every subscribed student. Any future paid layer can only ever sit in experience, personalisation, organisation, or support. Never in academic outcomes.",
  },
  {
    question: "What if competitors charge more for better academic results?",
    answer:
      "We treat that as a market we will not enter. Selling academic advantage is incompatible with academic integrity, with university partnership readiness, and with the long-term trust we are building with students and parents. We would rather grow more slowly than break this rule.",
  },
  {
    question: "What can Durmah charge extra for in the future?",
    answer:
      "Four areas, all non-academic: experience (voice quality, latency, polish), personalisation (adaptive pacing, deeper memory, dashboards), productivity (organisation, capacity, integrations), and insight or support (parent dashboards, progress summaries, wellbeing tooling). Any future paid layer requires explicit founder approval before going live.",
  },
];

export default function WhyWeDontSellBetterGrades() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: generateArticleSchema({
            headline: "Why Durmah Will Never Sell Better Grades",
            description: seo.description,
            datePublished: "2026-05-01",
            dateModified: "2026-05-01",
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: generateFAQSchema(faqs) }}
      />

      <LearnLayout
        title={seo.title}
        description={seo.description}
        slug="why-we-dont-sell-better-grades"
        category={["Ethics"]}
        canonicalUrl={seo.canonical}
      >
        <article className="prose prose-lg max-w-none">
          {/* Hero */}
          <div className="not-prose mb-12 p-8 bg-gradient-to-br from-rose-50 to-amber-50 rounded-2xl border border-rose-100">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-rose-600 rounded-xl text-white">
                <Shield className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  Why Durmah Will Never Sell Better Grades
                </h1>
                <p className="text-lg text-gray-600 mb-4">
                  A line we will not cross: written into product doctrine,
                  checked into the codebase, and visible to every student,
                  parent, and university we work with.
                </p>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="px-3 py-1 bg-white rounded-full border border-gray-200 text-gray-700">
                    7 min read
                  </span>
                  <span className="px-3 py-1 bg-white rounded-full border border-rose-200 text-rose-700">
                    Founder Note
                  </span>
                </div>
              </div>
            </div>
          </div>

          <h2>The market wants us to sell academic advantage. We refuse.</h2>
          <p>
            There is a clear commercial pattern in AI for education, and it is
            getting clearer every quarter. Free tier: capped, slow, generic.
            Premium tier: better answers, deeper analysis, exclusive content,
            faster path to a higher mark. The pricing page promises one thing.
            The marketing copy promises a better grade.
          </p>
          <p>
            We will never build that.
          </p>
          <p>
            Not because we cannot. The technical levers are obvious: gate
            higher-quality reasoning behind a paywall, withhold key academic
            features from base subscribers, market the upgrade in the language
            of better marks. We refuse because building those levers would
            betray everything Durmah is for.
          </p>

          <h2>The line, written down</h2>
          <p>
            We have committed this rule to the codebase so it survives every
            funding round, every product manager who joins after us, and every
            quarter where commercial pressure is high:
          </p>
          <blockquote>
            <p>
              <em>All subscribed students receive the full academic learning
              capability of Durmah equally. No paid tier, add-on, bundle,
              partnership, or promotion may grant better academic answers,
              better academic feedback, improved grading outcomes, exclusive
              academic content, or any other academic advantage.</em>
            </p>
          </blockquote>
          <p>
            You can read the full doctrine in our public philosophy and in
            our internal canonical document. The line is the same in both
            places, because the line cannot move.
          </p>

          <GuideCallout
            title="Read the full philosophy"
            body="Why we put the student at the centre, why AI is quiet support, and why fairness is built into our pricing, not bolted on."
            ctaText="See the Philosophy"
            ctaHref="/philosophy"
            icon={Heart}
            variant="indigo"
          />

          <h2>Three reasons this is also good business</h2>

          <h3>1. Trust capital compounds. Broken trust does not.</h3>
          <p>
            Students who suspect they are being upsold to academic advantage
            disengage. Parents who suspect a tool is selling shortcuts withdraw
            their support. Universities who suspect a platform monetises
            outcomes refuse to endorse it. Every one of those moments is
            unrecoverable. Trust earned by refusing the easy revenue line is
            the most durable trust in education.
          </p>

          <h3>2. Regulation is broadly moving in our direction.</h3>
          <p>
            The direction of travel for AI-in-education policy and
            academic-integrity guidance (across the UK, the EU, and further
            afield) is broadly the same: more scrutiny of pay-for-performance
            models, opaque academic advantage, and undisclosed AI authorship.
            By refusing those patterns from the start, Durmah is positioned with
            where the rules appear to be heading, rather than having to retrofit
            later.
          </p>

          <h3>3. Institutional partnerships need a clean conscience.</h3>
          <p>
            Universities cannot endorse tools that sell academic advantage.
            Ministries cannot recommend them. Schools cannot bundle them. By
            holding this line, we keep institutional channels open, channels
            that platforms built on premium-grades will never access.
          </p>

          <div className="not-prose my-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-6 bg-rose-50 rounded-2xl border border-rose-100">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-5 h-5 text-rose-600" />
                <h4 className="font-bold text-gray-900 m-0">What we do not sell</h4>
              </div>
              <ul className="text-sm text-gray-800 space-y-1">
                <li>Better grades</li>
                <li>Premium academic answers</li>
                <li>Exclusive academic advantage</li>
                <li>Hidden academic features</li>
              </ul>
            </div>
            <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <h4 className="font-bold text-gray-900 m-0">What we may charge for</h4>
              </div>
              <ul className="text-sm text-gray-800 space-y-1">
                <li>Experience (voice, latency, polish)</li>
                <li>Personalisation (memory, pacing, dashboards)</li>
                <li>Productivity (organisation, capacity)</li>
                <li>Insight &amp; support (parent dashboards, summaries)</li>
              </ul>
            </div>
          </div>

          <h2>What this means for our pricing today</h2>
          <p>
            Full Access purchases are available to eligible students after the standard trial ends, subject to the terms shown at checkout. Optional Voice Boost has a separate availability control. The independent writing workspace remains available without a purchase, and existing members retain saved-work access and billing controls. This article explains our pricing principles, not a promise that every optional add-on is currently available. See <a href="/pricing" className="underline">current availability and billing</a>.
          </p>
          <p>
            If we ever introduce additional paid layers, they will live only
            in the four non-academic areas above. Each one will require
            explicit founder approval before it goes live, recorded in our
            governance log. None will gate academic capability.
          </p>

          <h2>The promise, in one sentence</h2>
          <div className="not-prose my-8 p-8 bg-gradient-to-br from-emerald-50 to-indigo-50 rounded-2xl border border-emerald-100 text-center">
            <p className="text-xl md:text-2xl font-bold text-gray-900 leading-tight m-0">
              We sell support, not shortcuts. We expand on experience, not on
              academic outcomes.
            </p>
          </div>

          <GuideCallout
            title="See the full Durmah commitment"
            body="The Student Commitment section on our pricing page lays out the same promise, plain and visible, above every plan."
            ctaText="See Pricing &amp; Commitment"
            ctaHref="/pricing"
            icon={MessageSquare}
            variant="indigo"
          />

          <h2 id="faqs">Frequently Asked Questions</h2>
          <div className="not-prose space-y-4 my-6">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.question}</span>
                  <Zap className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-700 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </article>
      </LearnLayout>
    </>
  );
}
